import type { User } from '@supabase/supabase-js';
import { isSuperadmin } from './userRole';
import { resolveUserAccess, type UserAccess } from './userAccess';

export const SUPERADMIN_VIEW_STORAGE_KEY = 'boris_superadmin_view_store';

export interface SuperadminViewStore {
  storeId: string;
  storeName: string;
  storeActive: boolean;
  startedAt: string;
}

export function getSuperadminViewStore(): SuperadminViewStore | null {
  try {
    const raw = sessionStorage.getItem(SUPERADMIN_VIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SuperadminViewStore;
    if (!parsed?.storeId || typeof parsed.storeName !== 'string') return null;
    return {
      storeId: parsed.storeId,
      storeName: parsed.storeName,
      storeActive: parsed.storeActive !== false,
      startedAt: parsed.startedAt ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function setSuperadminViewStore(params: {
  storeId: string;
  storeName: string;
  storeActive: boolean;
}): void {
  const payload: SuperadminViewStore = {
    storeId: params.storeId,
    storeName: params.storeName,
    storeActive: params.storeActive,
    startedAt: new Date().toISOString(),
  };
  sessionStorage.setItem(SUPERADMIN_VIEW_STORAGE_KEY, JSON.stringify(payload));
}

export function clearSuperadminViewStore(): void {
  sessionStorage.removeItem(SUPERADMIN_VIEW_STORAGE_KEY);
}

/** Applies superadmin “view store” context on top of normal access resolution. */
export async function resolveEffectiveDashboardAccess(user: User): Promise<UserAccess> {
  const access = await resolveUserAccess(user);

  if (!isSuperadmin(access.role)) {
    return access;
  }

  const view = getSuperadminViewStore();
  if (!view) {
    return access;
  }

  if (!view.storeActive) {
    return {
      ...access,
      storeId: view.storeId,
      storeName: view.storeName,
      storeActive: false,
      canAccessDashboard: false,
      blockReason: 'store_inactive',
    };
  }

  return {
    ...access,
    storeId: view.storeId,
    storeName: view.storeName,
    storeActive: true,
    canAccessDashboard: true,
    blockReason: null,
  };
}

export function isSuperadminStoreViewActive(): boolean {
  return getSuperadminViewStore() !== null;
}
